import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import requestFrame from "../../shared/request-frame.js";
import type { GameState, Player } from "../../shared/types";
import Overlay from "./Overlay";
import * as gsf from "core/src/core/game-state-functions.js";
import { wormColors } from "core/src/core/constants.js";
import { flagUrl, isFlagCode } from "../../shared/flags";
import { TAG_IN, type IntroSchedule } from "../../shared/round-intro";
import { toView, useArenaView, viewScale } from "../arena-view";

// Round-start roll call, the board half: a tag with the player's flag and name
// under their head, appearing on that player's beat (see round-intro.ts),
// staying through the roll call and fading out with the others over its last
// beat, gone as the countdown starts. Positioned in map units over the arena,
// so it sits by the head at any arena size. Mount once per round (key it on
// the gameState).
//
// Timing is driven per frame from the render clock, the SAME clock the canvas
// draws the heads with, against the schedule: no CSS animation delays computed
// at mount, so a clock that hasn't settled yet when the round mounts (online,
// the first frames after game_start) can't fire the fade early, and the tags
// stay in step with the heads whatever the browser does with animations.
interface Props {
  gameState: GameState;
  players: Player[];
  // the roll call's timing for this game (round-intro.ts introSchedule)
  schedule: IntroSchedule;
  // the leader(s) coming into the round: their tags are the gold banner
  leaderIds?: string[];
  // the render clock (the one the canvas draws the heads with)
  getRenderTime: () => number;
  // First-run hints (first-run.ts), touch: these players' tags read `label`
  // instead of the name, in the worm's colour, blinking softly, and stay up
  // past the roll call until GO.
  youTag?: { ids: string[]; label: string };
}

// A head on the board, in layer pixels: a box no tag may cover.
interface HeadBox { x: number; y: number; r: number }

// A layout pass over the mounted tags (heads stand still through the roll
// call): every tag is kept INSIDE the arena (nudged back in from an edge via
// --tag-dx / --tag-dy on its transform), and a tag that would cover another
// tag or any player's head is pushed apart from it, top to bottom, moving down
// below whatever it overlaps (or up, when down would leave the arena). Runs
// again whenever the set of tags changes (online, a head's track can land a
// frame after the round mounts) and once the fonts are in (the gold banner's
// label is a webfont, so its width can change after the first pass), always
// from the tags' natural spots.
const TAG_GAP = 4;
const EDGE_PAD = 4;
function spreadTags(layer: HTMLElement, heads: HeadBox[]) {
  const L = layer.getBoundingClientRect();
  const els = Array.from(layer.querySelectorAll<HTMLElement>(".spawn-tag"));
  els.forEach((el) => {
    el.style.removeProperty("--tag-dx");
    el.style.removeProperty("--tag-dy");
  });
  const boxes = els.map((el) => {
    const r0 = el.getBoundingClientRect();
    // back inside the arena's edges
    let dx = 0, dy = 0;
    if (r0.left < L.left + EDGE_PAD) dx = L.left + EDGE_PAD - r0.left;
    else if (r0.right > L.right - EDGE_PAD) dx = L.right - EDGE_PAD - r0.right;
    if (r0.top < L.top + EDGE_PAD) dy = L.top + EDGE_PAD - r0.top;
    else if (r0.bottom > L.bottom - EDGE_PAD) dy = L.bottom - EDGE_PAD - r0.bottom;
    return { el, r: new DOMRect(r0.left + dx, r0.top + dy, r0.width, r0.height), dx, dy };
  });
  boxes.sort((a, b) => a.r.top - b.r.top);
  // Every head is in the way from the start (with a little air around it).
  const placed: DOMRect[] = heads.map((h) => {
    const pad = h.r + 2;
    return new DOMRect(L.left + h.x - pad, L.top + h.y - pad, pad * 2, pad * 2);
  });
  boxes.forEach((b) => {
    const r = b.r;
    let dy = 0;
    for (let guard = 0; guard < boxes.length + heads.length; guard++) {
      const top = r.top + dy, bottom = r.bottom + dy;
      const hit = placed.find((p) => r.left < p.right && r.right > p.left && top < p.bottom && bottom > p.top);
      if (!hit) break;
      const down = hit.bottom - top + TAG_GAP;
      // past the arena's bottom edge: go above the other one instead
      dy += bottom + down > L.bottom - EDGE_PAD ? hit.top - bottom - TAG_GAP : down;
    }
    if (b.dx) b.el.style.setProperty("--tag-dx", `${b.dx}px`);
    if (b.dy + dy) b.el.style.setProperty("--tag-dy", `${b.dy + dy}px`);
    placed.push(new DOMRect(r.left, r.top + dy, r.width, r.height));
  });
}

// 0..1: a tag's opacity at render time t. In over TAG_IN from its beat, held,
// then out over the schedule's fade with everyone else's.
export function tagAlpha(t: number, delay: number, fadeAt: number, fadeDur: number): number {
  if (t < delay) return 0;
  const fadeIn = Math.min(1, (t - delay) / TAG_IN);
  const fadeOut = t < fadeAt ? 1 : fadeDur > 0 ? Math.max(0, 1 - (t - fadeAt) / fadeDur) : 0;
  return Math.min(fadeIn, fadeOut);
}

const YOU_FADE = 0.4; // the "This is you" tag's fade at GO

export default function SpawnTags({ gameState, players, schedule, getRenderTime, leaderIds, youTag }: Props) {
  const delays = schedule.delays;
  const { fadeAt, fadeDur } = schedule;
  // the "you" tags fade out at GO instead of with the roll call
  const youIds = youTag ? youTag.ids : [];
  const youFadeAt = gameState.startPhaseTime || 0;
  // The render clock, never running backwards for the tags: online it can dip
  // or slew while it settles in the first frames after game_start, which made
  // the tags' fades step back and flicker.
  const maxNowRef = useRef(getRenderTime());
  const [now, setNow] = useState(() => maxNowRef.current);
  const layerRef = useRef<HTMLDivElement>(null);
  const { rotated } = useArenaView();
  // Re-render every frame on the render clock until the fade is done (then
  // unmount); the same clock the heads are drawn with, so tags and heads agree.
  const done = now >= fadeAt + fadeDur && (!youIds.length || now >= youFadeAt + YOU_FADE);
  useEffect(() => {
    if (done) return;
    let id = 0;
    const loop = () => {
      maxNowRef.current = Math.max(maxNowRef.current, getRenderTime());
      setNow(maxNowRef.current);
      id = requestFrame(loop);
    };
    id = requestFrame(loop);
    return () => window.cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  const map: any = gameState.map;
  const seen: Record<string, boolean> = {};
  const tags: React.ReactNode[] = [];
  // every head on the board, in map units (for the layout pass)
  const heads: { fx: number; fy: number; size: number }[] = [];
  if (!done && map) {
    (gameState as any).worms.forEach((worm: any) => {
      if (seen[worm.playerId]) return; // a twin: one tag per player
      const player = players.find((p) => p.id === worm.playerId);
      if (!player) return;
      const segmentId = worm.trackId || worm.playerId + "_" + worm.id;
      // The head stands still through the roll call, so where it IS is where it
      // spawned: read the track's first segment, which is there from game_start,
      // instead of the position at `now` (online, a clock ahead of or behind the
      // streamed segments returned nothing for a frame and the tag blinked).
      const segs: any[] = (gameState as any).wormPathSegments[segmentId] || [];
      const data: any = segs.length ? gsf.getWormPathSegmentDataAtTime(gameState, segmentId, segs[0].startTime) : null;
      if (!data) return;
      seen[worm.playerId] = true;
      const delay = delays[player.id];
      if (delay === undefined) return;
      const color = wormColors[player.colorId];
      // The tag hangs under the head, centred. Near the bottom edge it goes above
      // instead, and near a side edge it hangs from that side so it stays on the
      // arena.
      // Where the head shows in the arena's box (turned on an upright phone).
      const v = toView(data.position.x / map.width, data.position.y / map.height, rotated);
      const fx = v.x, fy = v.y;
      heads.push({ fx, fy, size: segs[0].size || 4 });
      const above = fy > 0.84;
      const edge = fx < 0.1 ? " spawn-tag-edge-left" : fx > 0.9 ? " spawn-tag-edge-right" : "";
      const lead = !!leaderIds && leaderIds.indexOf(player.id) !== -1;
      const you = youIds.indexOf(player.id) !== -1;
      tags.push(
        <div
          key={player.id}
          className={"spawn-tag" + (above ? " spawn-tag-above" : "") + edge + (lead && !you ? " spawn-tag-leader" : "") + (you ? " spawn-tag-you" : "")}
          style={{
            left: `${fx * 100}%`,
            top: `${fy * 100}%`,
            borderColor: lead && !you ? undefined : color,
            color: you ? color : undefined,
            // in on the player's beat, held, out over the call's last beat.
            // The "you" tag holds until GO, and gets its alpha as a variable
            // instead, so its blink (game-canvas.scss) can multiply it.
            opacity: you ? undefined : tagAlpha(now, delay, fadeAt, fadeDur),
            "--tag-alpha": you ? tagAlpha(now, delay, youFadeAt, YOU_FADE) : undefined,
          } as React.CSSProperties}
        >
          {lead && !you && (
            // the gold's shimmer: two soft light bands drifting at unrelated speeds
            <span className="spawn-tag-shimmer" aria-hidden="true">
              <span className="spawn-tag-band spawn-tag-band-1" />
              <span className="spawn-tag-band spawn-tag-band-2" />
            </span>
          )}
          {!you && isFlagCode(player.flag) ? <img className="spawn-tag-flag" src={flagUrl(player.flag!)} alt="" /> : null}
          <span className="spawn-tag-name">{you ? youTag!.label : player.name}</span>
        </div>
      );
    });
  }

  // The layout pass: on mount, whenever the set of tags changes, and once the
  // fonts are in. Heads are handed over in layer pixels.
  const layoutKey = tags.map((t: any) => t.key).join("|");
  const headsRef = useRef(heads);
  headsRef.current = heads;
  const rotatedRef = useRef(rotated);
  rotatedRef.current = rotated;
  useLayoutEffect(() => {
    const layer = layerRef.current;
    if (!layer || !map) return;
    const run = () => {
      if (!layerRef.current) return;
      const L = layerRef.current.getBoundingClientRect();
      const scale = viewScale(L.width, map, rotatedRef.current);
      spreadTags(layerRef.current, headsRef.current.map((h) => ({ x: h.fx * L.width, y: h.fy * L.height, r: h.size * scale })));
    };
    run();
    let cancelled = false;
    const fonts: any = typeof document !== "undefined" ? (document as any).fonts : null;
    if (fonts && fonts.ready && fonts.status !== "loaded") {
      fonts.ready.then(() => { if (!cancelled) run(); });
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutKey, rotated]);

  if (done || !map) return null;
  // A crowd gets smaller tags (game-canvas.scss): 40 full-size tags on the
  // roomy layout cover half the arena.
  const crowd = players.length > 16 ? 2 : players.length > 8 ? 1 : 0;
  return (
    <Overlay gameState={gameState} className="spawn-tags canvas-overlay-above-wall">
      <div ref={layerRef} className="spawn-tags-layer" data-crowd={crowd}>{tags}</div>
    </Overlay>
  );
}
